/**
 * Static catalog for the Products & Services stage matrix.
 *
 * The offering catalog is not modeled in the database; it is a content
 * snapshot owned by product and edited via PR. Source: the "Products &
 * services" Claude Design artifact
 * (https://claude.ai/artifact/UGQDqXC6t1yTvgiNoysXcu).
 *
 * Stages are cumulative: an offering included at a stage stays included at
 * every later stage. Before that stage it is either a paid add-on (`addOn`)
 * or not available. Columns are keyed by stage NAME, never position — DB
 * stage positions shift when the ladder changes (Pre-EP renumbered them).
 */

export const STAGES = ["Build", "Grow", "Lead", "Scale"] as const;
export type StageName = (typeof STAGES)[number];

export const CATEGORIES = [
  "Coaching",
  "Training",
  "Resources and Programs",
  "Market Insights",
  "Peer Groups",
  "Community",
] as const;
export type Category = (typeof CATEGORIES)[number];

export interface Offering {
  category: Category;
  name: string;
  description: string;
  /** First stage that includes the offering; null for add-on-only items. */
  includedFrom: StageName | null;
  /** Sold as a paid add-on at the stages before `includedFrom`. */
  addOn: boolean;
}

export type CellState = "included" | "add-on" | "not-available";

export function cellState(offering: Offering, stage: StageName): CellState {
  if (
    offering.includedFrom !== null &&
    STAGES.indexOf(stage) >= STAGES.indexOf(offering.includedFrom)
  ) {
    return "included";
  }
  return offering.addOn ? "add-on" : "not-available";
}

export type StageCounts = Record<StageName, number>;

/** Offerings first included at each stage. */
export function newAtStageCounts(offerings: Offering[]): StageCounts {
  const counts = { Build: 0, Grow: 0, Lead: 0, Scale: 0 };
  for (const o of offerings) {
    if (o.includedFrom !== null) {
      counts[o.includedFrom] += 1;
    }
  }
  return counts;
}

/** Offerings included at each stage, counting everything carried forward. */
export function cumulativeIncludedCounts(offerings: Offering[]): StageCounts {
  const fresh = newAtStageCounts(offerings);
  let running = 0;
  const counts = { Build: 0, Grow: 0, Lead: 0, Scale: 0 };
  for (const stage of STAGES) {
    running += fresh[stage];
    counts[stage] = running;
  }
  return counts;
}

export function addOnOnlyCount(offerings: Offering[]): number {
  return offerings.filter((o) => o.includedFrom === null).length;
}

/** Category intro lines, shown under the category band. */
export const categoryIntros: Partial<Record<Category, string>> = {
  "Market Insights":
    "Insights Reports unlock by stage. Lead Generation members get all twelve at any stage.",
};

export const offeringCatalog: Offering[] = [
  {
    category: "Coaching",
    name: "Initial Assessment: Financial",
    description:
      "A baseline review of your financials to set targets from Day One.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Initial Assessment: Marketing",
    description:
      "A baseline review of your marketing to find the fastest gains.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Onboarding Coaching",
    description:
      "A CertainPath onboarding coach sets your foundations from Day One.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Coach Connect: Field",
    description:
      "Group coaching calls on topics related to your field operations.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Specialty Coaching — Financial",
    description:
      "Sessions with a CertainPath financial expert on your numbers and targets.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Specialty Coaching — Marketing",
    description:
      "Sessions with a CertainPath marketing expert on your plan and spend.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Coach Connect: Management",
    description: "Group coaching calls on trending managerial focused topics.",
    includedFrom: "Lead",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Specialty Coaching — Field Management & Optimization",
    description:
      "Expert coaching to tighten field management and get more from every truck.",
    includedFrom: "Lead",
    addOn: false,
  },
  {
    category: "Coaching",
    name: "Field Coach",
    description:
      "Virtual ride-alongs to coach your team from what really happens on every call.",
    includedFrom: null,
    addOn: true,
  },
  {
    category: "Coaching",
    name: "SOAR",
    description:
      "On-site Strategic Review plus follow-up coaching for results.",
    includedFrom: null,
    addOn: true,
  },
  {
    category: "Training",
    name: "Executive Perspective",
    description:
      "The most powerful training week; return as many times as you want.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "Dispatching for Profits Course (In-Person or Virtual)",
    description: "Dispatching for Profits puts the right tech on every call.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "HVAC Sales Course and Reinforcements (In-Person or Virtual)",
    description:
      "CertainPath Sales System training for advisors and selling techs, plus reinforcements.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "Roofing Sales Course and Reinforcements (In-Person or Virtual)",
    description:
      "CertainPath Sales System training for roofing sales specialists, plus reinforcements.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "Service Essentials Course and Reinforcements (In-Person or Virtual)",
    description:
      "Our signature course in the CertainPath service system, plus scorecards and reinforcement videos.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "Telephone Essentials Course (In-Person or Virtual)",
    description: "Master how your team answers and books more calls than ever.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Training",
    name: "Advanced Selling Course",
    description:
      "Dive into the psychology of sales techniques your team can use today.",
    includedFrom: "Grow",
    addOn: true,
  },
  {
    category: "Training",
    name: "Electricians Advanced Course and Reinforcements (In-Person or Virtual)",
    description:
      "Take electricians beyond the basics to master selling in the home, plus reinforcements.",
    includedFrom: "Grow",
    addOn: true,
  },
  {
    category: "Training",
    name: "Fundamentals of Drain Cleaning",
    description:
      "Hands-on drain cleaning class covering technical and sales skills.",
    includedFrom: "Grow",
    addOn: true,
  },
  {
    category: "Training",
    name: "Impact Workshops",
    description:
      "Short, focused workshops that tackle one high-impact topic at a time.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Training",
    name: "Plumbers Advanced Course and Reinforcements (In-Person or Virtual)",
    description:
      "Take plumbing techs beyond the basics to master selling in the home, plus reinforcements.",
    includedFrom: "Grow",
    addOn: true,
  },
  {
    category: "Training",
    name: "Call Center Management Course",
    description:
      "Give call center managers the skills to hire, coach, and lead a booking team.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Developing Effective Managers Course",
    description:
      "Give your management team the foundation to be successful managers.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Emotional Intelligence Course",
    description:
      "Build a strong EQ for leading, communicating, and selling in the home.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Foundational Leadership Course",
    description: "Discover the foundation of becoming a more effective leader.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Planning for Success Course",
    description:
      "An in-depth course to help you budget and plan the year ahead.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Recruit and Retain Top Talent Course",
    description:
      "Learn to hire the right people and build a culture that retains them.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Sales Management Course",
    description:
      "Everything a sales manager needs to grow, manage, and coach your sales team.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Service Management Course",
    description:
      "Everything a service manager needs to lead a growing service team.",
    includedFrom: "Lead",
    addOn: true,
  },
  {
    category: "Training",
    name: "Developing Winning Trainers Course",
    description:
      "Give your managers the skills to become winning trainers in-house.",
    includedFrom: "Scale",
    addOn: true,
  },
  {
    category: "Training",
    name: "RevenueAscent",
    description:
      "Ongoing reinforcement training to make results stick along with management review calls.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "The Accountability Group (TAG) — Stage-Based Groups",
    description:
      "Join a group of your peers to gain accountability and work together to achieve results.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "Call Center Mastery Group",
    description:
      "Coaching and accountability calls for call center managers. Join an existing group; new groups start as demand allows.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "Leadership Mastery Group",
    description:
      "A recurring group focused on building practical leadership and management skills across your team. Join an existing group; new groups start as demand allows.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "Marketing Mastery Group",
    description:
      "Training and accountability calls for your marketing lead. Join an existing group; new groups start as demand allows.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "Sales Management Mastery Group",
    description:
      "Training and accountability calls for you and your sales manager. Join an existing group; new groups start as demand allows.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "Service Management Mastery Group",
    description:
      "Training and accountability calls for your service managers. Join an existing group; new groups start as demand allows.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Peer Groups",
    name: "The Lion's Pride (Group-Based)",
    description:
      "Build your leadership skills in small group training with fellow members. Each cohort is kept small and every participant committed. New cohorts start as demand allows. Two seats per year.",
    includedFrom: "Scale",
    addOn: true,
  },
  {
    category: "Peer Groups",
    name: "The Lion's Pride (Individual-Based)",
    description:
      "A leadership journey matched to your business and life. Sold separately because it's built on personal, one-to-one discussion and your own commitment.",
    includedFrom: null,
    addOn: true,
  },
  {
    category: "Resources and Programs",
    name: "HUB Resource Library — Build Stage Resources",
    description:
      "The CertainPath system of tools, resources, and courses curated for Build.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "Partner Network",
    description:
      "Tap into CertainPath's group buying power to earn industry-leading rebates.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "Unique Selling Propositions (Trademarks)",
    description:
      "Use members-only trademarks to build trust and stand out locally.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "HUB Resource Library PLUS Grow Stage Resources",
    description: "Adds the tools, resources, and courses curated for Grow.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "HUB Resource Library PLUS Lead Stage Resources",
    description: "Adds the tools, resources, and courses curated for Lead.",
    includedFrom: "Lead",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "HUB Resource Library PLUS Scale Stage Resources",
    description: "Adds the tools, resources, and courses curated for Scale.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Resources and Programs",
    name: "ServiceLine Software",
    description:
      "Field management software to run a profitable operation built on the CertainPath system.",
    includedFrom: null,
    addOn: true,
  },
  {
    category: "Market Insights",
    name: "Lead Generation",
    description:
      "CertainPath's marketing service with campaigns that turn data into measurable revenue. Includes all twelve Insights Reports at any stage.",
    includedFrom: null,
    addOn: true,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Average Invoice Comparison",
    description:
      "See how your average invoice has trended over different periods.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Revenue by Invoice Age",
    description:
      "Make financial decisions from an aging view of outstanding revenue.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Sales — New vs Existing Customers",
    description:
      "Focus marketing by revenue split between new and existing customers.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Sales by Year & Month",
    description:
      "Spot busy times and shoulder months with a time-series revenue view.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Percentage of New Customers by ZIP",
    description:
      "See where your new customers come from to focus your marketing.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Households by ZIP",
    description:
      "Spot the true potential in your market and respond accordingly.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Sales by ZIP Code",
    description:
      "See which zip codes drive results with total sales by zip code.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Sales from New Customers by ZIP & Year",
    description:
      "See where new customer sales come from to guide service and marketing.",
    includedFrom: "Grow",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Existing Customer Replacement Efficiency",
    description:
      "See how often existing-customer calls turn into replacement sales.",
    includedFrom: "Lead",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: New Customer Replacement Efficiency",
    description:
      "See how often new-customer calls turn into replacement sales.",
    includedFrom: "Lead",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Lifetime Value by Tier",
    description:
      "See the long-term lifetime value of each tier of your customers.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Market Insights",
    name: "Insights Report: Total Addressable Market",
    description:
      "See your business potential through the addressable market near you.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Community",
    name: "Fall Event Attendance",
    description:
      "Connect and network with fellow members in unbeatable locations.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Community",
    name: "Member Facebook Groups",
    description:
      "Trade-specific member groups for HVAC, plumbing, roofing, and electrical.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Community",
    name: "Spring Expo Attendance",
    description:
      "The industry's best event: gain insights and network with 1,000+ members.",
    includedFrom: "Build",
    addOn: false,
  },
  {
    category: "Community",
    name: "Awards Gala Team Table",
    description:
      "A reserved premium team table at the CertainPath Awards Gala.",
    includedFrom: "Scale",
    addOn: false,
  },
  {
    category: "Community",
    name: "Eagles Summit Attendance",
    description:
      "Our event for our largest members to network and learn together.",
    includedFrom: "Scale",
    addOn: false,
  },
];
